import type { AuditEvent } from '../api/types'
import { formatDateTime } from './common'
import { DataTable, type Column } from './table'
import { t } from '../i18n/lingua'

/**
 * La tabella condivisa degli eventi di audit — accessi e operazioni.
 *
 * La usano due schermate: la scheda del tenant (console) e il registro accessi
 * di piattaforma (amministrazione). Le colonne sono le stesse; cambia solo se
 * si mostra il tenant (che nella scheda di un tenant è ovvio, e nella vista di
 * piattaforma è la colonna che conta).
 *
 * L'ordinamento e il filtro per colonna li dà `DataTable`; la paginazione è di
 * chi la usa, perché è lato server (l'audit cresce senza limite).
 */
export function AuditTable({
  events,
  showTenant = false,
}: {
  events: AuditEvent[]
  showTenant?: boolean
}) {
  const colonne: Column<AuditEvent>[] = [
    {
      key: 'occurred_at', header: t('Quando'), type: 'date',
      value: (e) => e.occurred_at,
      render: (e) => (e.occurred_at ? formatDateTime(e.occurred_at) : '—'),
    },
    {
      key: 'category', header: t('Tipo'), value: (e) => e.category,
      render: (e) => <CategoriaBadge category={e.category} />,
    },
    {
      key: 'action', header: t('Azione'), value: (e) => e.action,
      render: (e) => <AzioneEtichetta action={e.action} />,
    },
    {
      key: 'actor', header: t('Chi'),
      value: (e) => e.actor_email || e.actor_subject || '',
      render: (e) => e.actor_email
        ? <span>{e.actor_email}</span>
        : <span className="muted">{e.actor_subject || t('sconosciuto')}</span>,
    },
    ...(showTenant ? [{
      key: 'tenant', header: t('Tenant'),
      value: (e: AuditEvent) => e.tenant_id || '',
      render: (e: AuditEvent) => e.tenant_id
        ? <span className="mono">{e.tenant_id.slice(0, 8)}</span>
        : <span className="muted">{t('piattaforma')}</span>,
    } as Column<AuditEvent>] : []),
    {
      key: 'target', header: t('Oggetto'),
      value: (e) => e.target || '',
      render: (e) => e.target
        ? <span className="mono">{e.target}{e.status_code ? ` · ${e.status_code}` : ''}</span>
        : <span className="muted">—</span>,
    },
    {
      key: 'outcome', header: t('Esito'), value: (e) => e.outcome,
      render: (e) => <EsitoBadge outcome={e.outcome} />,
    },
    {
      key: 'source_ip', header: t('Origine'), value: (e) => e.source_ip || '',
      render: (e) => e.source_ip
        ? <span className="mono">{e.source_ip}</span>
        : <span className="muted">—</span>,
    },
  ]

  return (
    <DataTable
      rows={events}
      columns={colonne}
      rowKey={(e) => e.id}
      initialSort={{ key: 'occurred_at', desc: true }}
    />
  )
}

function CategoriaBadge({ category }: { category: string }) {
  if (category === 'access') return <span className="badge badge--info">{t('accesso')}</span>
  return <span className="badge badge--muted">{t('operazione')}</span>
}

function EsitoBadge({ outcome }: { outcome: string }) {
  if (outcome === 'ok') return <span className="badge badge--ok">{t('ok')}</span>
  if (outcome === 'failed') return <span className="badge badge--warn">{t('respinto')}</span>
  return <span className="badge badge--error">{t('errore')}</span>
}

/** L'azione in parole: per l'accesso una frase, per l'operazione il metodo. */
function AzioneEtichetta({ action }: { action: string }) {
  const mappa: Record<string, string> = {
    login: t('accesso'),
    login_failed: t('accesso fallito'),
    mfa_failed: t('codice MFA errato'),
    tenant_switched: t('cambio tenant'),
    password_reset: t('password reimpostata dal collegamento'),
    password_set: t('password impostata dall’invito'),
    password_link_shown: t('link password mostrato a un amministratore'),
  }
  const etichetta = mappa[action]
  if (etichetta) return <span>{etichetta}</span>
  return <span className="mono">{action}</span>
}

/** Paginazione lato server: «N–M di TOTAL» e i due passi. Condivisa fra la
 *  scheda del tenant e il registro di piattaforma. */
export function Paginazione({
  total,
  limit,
  offset,
  onVai,
}: {
  total: number
  limit: number
  offset: number
  onVai: (offset: number) => void
}) {
  const da = total === 0 ? 0 : offset + 1
  const a = Math.min(offset + limit, total)
  return (
    <div className="toolbar" style={{ marginTop: 12, justifyContent: 'space-between' }}>
      <span className="muted">
        {t('{da}–{a} di {total}', { da, a, total })}
      </span>
      <span className="toolbar" style={{ gap: 6 }}>
        <button type="button" className="secondary"
                disabled={offset <= 0}
                onClick={() => onVai(Math.max(0, offset - limit))}>
          {t('Precedenti')}
        </button>
        <button type="button" className="secondary"
                disabled={a >= total}
                onClick={() => onVai(offset + limit)}>
          {t('Successivi')}
        </button>
      </span>
    </div>
  )
}
